'use client'

import Link from 'next/link'
import { MC, SERIF } from '@/lib/mycio/theme'
import { useMyCIOLanguage } from '@/lib/mycio/MyCIOLanguageContext'
import { coverageT } from '@/lib/mycio/coverageI18n'
import { coverageTopics, jurisdictionCoverage } from '@/lib/mycio/marketingCoverage'
import { localizedCountryName, type CountryDetail } from '@/lib/mycio/coverageDetail'
import { BODY, CARD, EYEBROW, EstimateFooter, Flag, H1, H2, H3, LABEL, LEAD, TierPill } from './parts'

type Scope = (typeof jurisdictionCoverage)[number] | null

/**
 * /coverage/<code>, rendered in the reader's language (see CoverageIndexView for why this is a client
 * component). Every sentence is derived from code (coverageDetail.ts) and goes through `t.s()`.
 */
export default function CountryCoverageView({ country: c, scope, others }: { country: CountryDetail; scope: Scope; others: CountryDetail[] }) {
  const { language } = useMyCIOLanguage()
  const t = coverageT(language)
  const li: React.CSSProperties = { ...BODY, fontSize: 15.5, padding: '10px 0', borderTop: '1px solid #EFE9DC' }
  // Intl.DisplayNames renders the country in the reader's language; `c.name` stays the English lookup key.
  const shown = localizedCountryName(c.code, language, c.name)
  const name = { name: shown }

  return (
    <>
      <section className="mc-container mc-pad-hero" style={{ maxWidth: 1180, margin: '0 auto', padding: '84px 32px 48px' }}>
        <Link href="/coverage" className="mc-link-plain" style={{ display: 'inline-block', fontSize: 14, fontWeight: 600, color: MC.forest, marginBottom: 22 }}>{t.ui('backToAll')}</Link>
        <p style={EYEBROW}>{t.ui('countryEyebrow')}</p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', marginBottom: 14 }}>
          <Flag slug={c.slug} />
          <h1 className="mc-finalcta-h" style={{ ...H1, margin: 0 }}>{shown}</h1>
          <TierPill tier={c.tier} size={13} t={t} />
        </div>
        <p style={LEAD}>
          {c.tier === 'validated'
            ? `${t.ui('validatedSentence')} ${t.ui('stillEstimates')}`
            : `${c.memberSentence ? t.s(c.memberSentence) : ''} ${t.ui('stillEstimates')}`}
        </p>
        <p style={{ fontSize: 13.5, color: MC.muted2, margin: 0 }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <span style={{ width: 8, height: 8, borderRadius: 999, background: c.tier === 'validated' ? MC.forest : MC.clay }} />
            {c.tier === 'validated' ? t.ui('validated') : t.ui('provisional')} — {c.tier === 'validated' ? t.ui('validatedNote') : t.ui('provisionalNote')}
          </span>
        </p>
      </section>

      {/* 1 · What is encoded — tax year / edition, law, rules (marketingCoverage.ts jurisdictionCoverage). */}
      <section className="mc-container" style={{ maxWidth: 1180, margin: '0 auto', padding: '0 32px 56px' }}>
        <h2 style={H2}>{t.ui('encodedTitle')}</h2>
        {scope ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 14 }}>
            <div style={CARD}><p style={LABEL}>{t.ui('editionLabel')}</p><p style={BODY}>{t.s(scope.edition)}.</p></div>
            <div style={CARD}><p style={LABEL}>{t.ui('lawLabel')}</p><p style={BODY}>{t.s(scope.law)}.</p></div>
            <div style={CARD}><p style={LABEL}>{t.ui('rulesLabel')}</p><p style={BODY}>{t.s(scope.rules)}</p></div>
            <div style={{ ...CARD, gridColumn: '1 / -1' }}><p style={LABEL}>{t.ui('limitsLabel')}</p><p style={BODY}>{t.s(scope.limits)}</p></div>
          </div>
        ) : (
          <p style={BODY}>{t.ui('noScope')}</p>
        )}
      </section>

      {/* 2 · What the engine covers — the six topic areas, same in every country. */}
      <section style={{ background: '#EEE8DC', borderTop: `1px solid ${MC.border}`, borderBottom: `1px solid ${MC.border}` }}>
        <div className="mc-container mc-pad-section" style={{ maxWidth: 1180, margin: '0 auto', padding: '64px 32px' }}>
          <h2 style={H2}>{t.ui('enginesEyebrow')}</h2>
          <p style={{ ...LEAD, marginBottom: 28 }}>{t.ui('sixAreasCountryLead')}</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 14 }}>
            {coverageTopics.map((tp) => (
              <article key={tp.title} style={CARD}>
                <h3 style={H3}>{t.s(tp.title)}</h3>
                <p style={{ ...BODY, fontSize: 14.5, marginBottom: 8 }}><strong style={{ color: MC.forest }}>{t.ui('included')}</strong> {t.s(tp.included)}</p>
                <p style={{ ...BODY, fontSize: 14.5, marginBottom: 8 }}><strong style={{ color: MC.inkSoft }}>{t.ui('assumed')}</strong> {t.s(tp.assumption)}</p>
                <p style={{ ...BODY, fontSize: 14.5 }}><strong style={{ color: MC.clay }}>{t.ui('notCovered')}</strong> {t.s(tp.excluded)}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* 3 · Still being verified · 4 · When MyCIO refuses. */}
      <section className="mc-container" style={{ maxWidth: 1180, margin: '0 auto', padding: '64px 32px 0' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 28 }}>
          <div>
            <h2 style={H2}>{t.ui('verifyingTitle')}</h2>
            {c.verifying.length ? (
              <ul style={{ listStyle: 'none', margin: 0, padding: 0, borderBottom: '1px solid #EFE9DC' }}>
                {c.verifying.map((v) => <li key={v} style={li}>{t.s(v)}</li>)}
              </ul>
            ) : (
              <p style={BODY}>{t.ui('noOpenItems', name)}</p>
            )}
          </div>
          <div>
            <h2 style={H2}>{t.ui('refusesTitle')}</h2>
            {c.refuses.length ? (
              <>
                <p style={{ ...BODY, marginBottom: 8, fontWeight: 600, color: MC.ink }}>{t.ui('refusesLead')}</p>
                <ul style={{ listStyle: 'none', margin: 0, padding: 0, borderBottom: '1px solid #EFE9DC' }}>
                  {c.refuses.map((r) => <li key={r} style={li}>…{t.s(r)}</li>)}
                </ul>
              </>
            ) : (
              <p style={BODY}>{t.ui('noRefusals', name)}</p>
            )}
          </div>
        </div>
        <div style={{ ...CARD, marginTop: 28 }}>
          <p style={LABEL}>{t.ui('crossBorderLabel')}</p>
          {c.crossBorder.map((s) => <p key={s} style={{ ...BODY, fontSize: 15, marginBottom: 6 }}>{t.s(s)}</p>)}
        </div>
      </section>

      <section className="mc-container" style={{ maxWidth: 1180, margin: '0 auto', padding: '0 32px 96px' }}>
        <EstimateFooter t={t} />
        <p style={{ ...LABEL, marginTop: 40 }}>{t.ui('otherCountries')}</p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 9 }}>
          {others.map((o) => (
            <Link key={o.code} href={`/coverage/${o.slug}`} className="mc-flagchip" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px 8px 15px', background: MC.surface, border: `1px solid ${MC.border}`, borderRadius: 999 }}>
              <Flag slug={o.slug} />
              <span style={{ fontSize: 14, color: '#3C382F', whiteSpace: 'nowrap', fontFamily: SERIF }}>{localizedCountryName(o.code, language, o.name)}</span>
              <TierPill tier={o.tier} size={11} t={t} />
            </Link>
          ))}
        </div>
      </section>
    </>
  )
}
